import { useEffect, useState } from "react";
import { MessageSquare, Plus, Trash2, User, ShieldAlert } from "lucide-react";
import dayjs from "dayjs";

import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/input";
import { DialogModal, ConfirmPopupModal } from "../../../components/ui/dialogModal";
import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { EMPLOYEE_REMARKS } from "../../../constants/api";
import { checkUserPermissionFunc } from "../../../components/common/CommonFunctions";
import { cn } from "../../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../../components/ui/fieldStyles";
import { isOwnEmployeeProfile, REMARKS_SELF_VIEW_MESSAGE } from "../../../utils/employeeProfile";

const UNAUTHORIZED_REMARKS_MESSAGE = "You are not authorized to view remarks";
const REMARK_MAX_LENGTH = 500;

const EmployeeRemarksContent = ({
    employeeID,
    compact = false,
    heading = "Employee Remarks",
    addButtonLabel = "Add Remark",
    dialogTitle = "Add Remark",
    allowAdd = false,
    serverDecidesAccess = false,
}) => {
    const { fetch, create, remove } = useApiMethods();
    const viewingOwnProfile = isOwnEmployeeProfile(employeeID);

    const remarksPerm = checkUserPermissionFunc("employee", "employee_remarks", "SubMenu");
    const employeesPerm = checkUserPermissionFunc("employee", "employees", "SubMenu");
    const canViewRemarks = serverDecidesAccess || viewingOwnProfile || remarksPerm.permission || employeesPerm.edit || employeesPerm.permission;
    const canAddRemark = !viewingOwnProfile && Boolean(remarksPerm.add);
    const canDeleteRemark = !viewingOwnProfile && (remarksPerm.edit || employeesPerm.edit);

    const [loading, setLoading] = useState(false);
    const [remarks, setRemarks] = useState([]);
    const [accessDenied, setAccessDenied] = useState(!canViewRemarks);
    const [accessMessage, setAccessMessage] = useState(
        !canViewRemarks ? UNAUTHORIZED_REMARKS_MESSAGE : "",
    );
    const [dialogOpen, setDialogOpen] = useState(false);
    const [remarkText, setRemarkText] = useState("");
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const loadRemarks = async () => {
        if (!employeeID || !canViewRemarks) {
            setAccessDenied(true);
            setAccessMessage(UNAUTHORIZED_REMARKS_MESSAGE);
            return;
        }
        setLoading(true);
        setAccessDenied(false);
        setAccessMessage("");
        try {
            const apiData = await fetch(EMPLOYEE_REMARKS, { employee_id: employeeID });
            if (apiData?.status === "success") {
                setRemarks(apiData.data || []);
            } else {
                const message = apiData?.message || "Failed to load remarks";
                const unauthorized = message.toLowerCase().includes("not authorized");
                if (unauthorized) {
                    setAccessDenied(true);
                    setAccessMessage(message);
                    ToastMsg("error", message);
                } else {
                    ToastMsg("error", message);
                }
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (employeeID) {
            loadRemarks();
        }
        // fetch from useApiMethods is not referentially stable — only re-load when employee changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID, viewingOwnProfile]);

    const handleAddRemark = async () => {
        if (viewingOwnProfile) {
            ToastMsg("error", UNAUTHORIZED_REMARKS_MESSAGE);
            return;
        }
        const text = remarkText.trim();
        if (text.length < 3) {
            ToastMsg("error", "Remark must be at least 3 characters");
            return;
        }
        if (text.length > REMARK_MAX_LENGTH) {
            ToastMsg("error", `Remark must be at most ${REMARK_MAX_LENGTH} characters`);
            return;
        }
        setSaving(true);
        try {
            const apiData = await create(EMPLOYEE_REMARKS, {
                employee_id: employeeID,
                remark: text,
            });
            if (apiData?.status === "success") {
                ToastMsg("success", apiData.message || "Remark added");
                setDialogOpen(false);
                setRemarkText("");
                await loadRemarks();
            } else {
                ToastMsg("error", apiData?.message || "Failed to add remark");
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRemark = async () => {
        if (!deleteTarget?.id) return;
        setDeleting(true);
        try {
            const apiData = await remove(`${EMPLOYEE_REMARKS}?id=${deleteTarget.id}`);
            if (apiData?.status === "success") {
                ToastMsg("success", apiData.message || "Remark deleted");
                setDeleteTarget(null);
                await loadRemarks();
            } else {
                ToastMsg("error", apiData?.message || "Failed to delete remark");
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setDeleting(false);
        }
    };

    if (accessDenied) {
        return (
            <div className="space-y-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-sidebar-primary" />
                        {heading}
                    </h3>
                </div>
                <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-900/20 p-8 text-center">
                    <ShieldAlert className="h-10 w-10 mx-auto text-amber-600 dark:text-amber-400 mb-3" />
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {accessMessage || UNAUTHORIZED_REMARKS_MESSAGE}
                    </p>
                    {!viewingOwnProfile && (
                    <p className="text-xs text-muted-foreground mt-2">
                        Remarks are written by managers or HR and are hidden when the remarks permission is off.
                    </p>
                    )}
                </div>
            </div>
        );
    }

    if (loading && remarks.length === 0) {
        return (
            <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-sidebar-primary" />
                        {heading}
                    </h3>
                    {!compact && (
                    <p className="text-sm text-muted-foreground mt-1">
                        Historical notes visible to authorized managers and HR throughout the employee&apos;s tenure.
                    </p>
                    )}
                </div>
                {canAddRemark && (
                    <Button onClick={() => setDialogOpen(true)} className="gap-2">
                        <Plus className="h-4 w-4" />
                        {addButtonLabel}
                    </Button>
                )}
            </div>

            {remarks.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
                    <MessageSquare className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm text-muted-foreground">No remarks recorded yet.</p>
                    {canAddRemark && (
                        <Button variant="outline" className="mt-4 gap-2" onClick={() => setDialogOpen(true)}>
                            <Plus className="h-4 w-4" />
                            Add first remark
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {remarks.map((item) => (
                        <div
                            key={item.id}
                            className={cn(
                                "rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 p-4",
                            )}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                                        {item.remark}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
                                        <span className="inline-flex items-center gap-1">
                                            <User className="h-3.5 w-3.5" />
                                            {item.author_name || item.created_by || "Unknown"}
                                        </span>
                                        <span>
                                            {item.created_date
                                                ? dayjs(item.created_date).format("DD-MM-YYYY hh:mm A")
                                                : "-"}
                                        </span>
                                    </div>
                                </div>
                                {canDeleteRemark && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0"
                                        onClick={() => setDeleteTarget(item)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <DialogModal
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setRemarkText("");
                }}
                onSave={handleAddRemark}
                title={dialogTitle}
                saveLabel="Save Remark"
                loading={saving}
                width="480px"
            >
                <div className="space-y-2">
                    <label className={modalFieldLabelClass}>
                        Remark
                    </label>
                    <Textarea
                        value={remarkText}
                        onChange={(e) => setRemarkText(e.target.value)}
                        placeholder="Enter remark or note about this employee..."
                        rows={5}
                        className="resize-none"
                        maxLength={REMARK_MAX_LENGTH}
                        showCharCount
                    />
                    <p className="text-xs text-muted-foreground">
                        Remarks are append-only and form a permanent historical record (max {REMARK_MAX_LENGTH} characters).
                    </p>
                </div>
            </DialogModal>

            <ConfirmPopupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDeleteRemark}
                title="Delete Remark"
                description="This remark will be removed from the employee profile. This action cannot be undone."
                confirmLabel="Delete"
                type="danger"
                destructive
                loading={deleting}
            />
        </div>
    );
};

export default EmployeeRemarksContent;
